import PersonIcon from "@mui/icons-material/Person";
import { Box, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import type { AgentSummary } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAgentFullName } from "./utils";

const CARD_RADIUS = "14px";

type Props = {
  readonly sharedWithAgent: AgentSummary | null;
  readonly disabled: boolean;
  readonly onTakeBack: () => void;
};

export const SharingCurrentAdvisor = ({ sharedWithAgent, disabled, onTakeBack }: Props) => {
  const { t } = useResource();

  if (!sharedWithAgent) {
    return (
      <HStack
        alignItems="center"
        spacing={Spacing.L}
        py={Spacing.XL}
        px="18px"
        border="1px dashed"
        borderColor="divider"
        borderRadius={CARD_RADIUS}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          width={48}
          height={48}
          borderRadius="50%"
          bgcolor="grey.100"
          color="text.secondary"
        >
          <PersonIcon />
        </Flex>
        <Box>
          <Typography fontSize={15} fontWeight={500}>
            {t(Resources.AnalysisDetail.Sharing.NotShared.Title)}
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.NotShared.Text)}
          </Typography>
        </Box>
      </HStack>
    );
  }

  const fullName = getAgentFullName(sharedWithAgent);

  return (
    <HStack
      alignItems="center"
      justifyContent="space-between"
      spacing={Spacing.L}
      py={Spacing.XL}
      px="18px"
      border={1}
      borderRadius={CARD_RADIUS}
      sx={(theme) => ({ bgcolor: theme.colors.brandLighter, borderColor: theme.colors.brandLightBorder })}
    >
      <HStack alignItems="center" spacing={Spacing.L} minWidth={0}>
        <UserInitialsAvatar firstName={sharedWithAgent.firstName} surname={sharedWithAgent.lastName} color="primary" variant="large" />
        <Box minWidth={0}>
          <Typography fontSize={17} fontWeight={700} noWrap>
            {fullName}
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.AdvisorId, { advisorId: sharedWithAgent.ssoId })}
          </Typography>
        </Box>
      </HStack>
      <Button disabled={disabled} onClick={onTakeBack} sx={{ flexShrink: 0 }}>
        {t(Resources.AnalysisDetail.Sharing.TakeOver)}
      </Button>
    </HStack>
  );
};
